import { CdkDragDrop, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';

import { NgIf, NgFor, NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  Injector,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { IdentifierTriggerComponent } from '@next/core-lib/components';
import { AgrNumberDirective } from '@next/core-lib/form';
import {
  AgrNumberPipe,
  LocaleService,
  TranslatePipe,
  AgrDatePipe,
  AgrCurrencyPipe,
} from '@next/core-lib/i18n';

import {
  TableColumnTypes,
  TableColumnTypesFixed,
  TableStickyTypes,
  TableColumnStickyTypes,
  TableFooterValueTypes,
  TableConfig,
} from '../../table.model';

@Component({
  selector: 'app-table-desktop',
  templateUrl: './table-desktop.component.html',
  styleUrls: ['./table-desktop.component.scss'],
  imports: [
    NgIf,
    MatTable,
    MatSort,
    CdkDropList,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatFooterCellDef,
    MatFooterCell,
    MatCheckbox,
    ReactiveFormsModule,
    MatIconButton,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    NgFor,
    MatMenuItem,
    MatSortHeader,
    IdentifierTriggerComponent,
    NgTemplateOutlet,
    MatFormField,
    MatInput,
    AgrNumberDirective,
    NgClass,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    CdkDrag,
    MatFooterRowDef,
    MatFooterRow,
    TranslatePipe,
    AgrDatePipe,
    AgrCurrencyPipe,
    AgrNumberPipe,
    AsyncPipe,
  ],
})
export class TableDesktopComponent {
  private readonly locale = inject(LocaleService);
  private readonly decimalPipe = inject(AgrNumberPipe);
  private readonly injector = inject(Injector);
  private readonly customFootersCache = new WeakMap<
    TableConfig,
    Array<{ id: string; referenceId: string }[]>
  >();
  private readonly customFooterRowIdsCache = new WeakMap<TableConfig, string[][]>();
  private readonly footerVisibilityCache = new WeakMap<TableConfig, boolean[]>();

  public readonly tableColumnTypes = TableColumnTypes;
  public readonly tableColumnTypesFixed = TableColumnTypesFixed;
  public readonly tableStickyTypes = TableStickyTypes;
  public readonly tableColumnStickyTypes = TableColumnStickyTypes;

  @ViewChild('template') template: TemplateRef<unknown>;
  @ViewChild('table') tableRef: MatTable<unknown>;
  @ViewChild(MatSort) sort: MatSort;

  private _lastConfig: TableConfig;
  private _pendingConfig: TableConfig | undefined;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  initTable(config: TableConfig) {
    if (config !== this._lastConfig) {
      this._lastConfig = config;
      this._pendingConfig = config;
      afterNextRender(
        () => {
          if (this._pendingConfig !== config) {
            return;
          }

          this._pendingConfig = undefined;
          if (!config.dataSource.sort) {
            config.dataSource.sort = this.sort;
          }
        },
        { injector: this.injector },
      );
    } else if (!config.dataSource.sort && this.sort && this._pendingConfig !== config) {
      config.dataSource.sort = this.sort;
    }

    return true;
  }

  trackByIndex = (i: number) => i;

  getCustomFooters(config: TableConfig): Array<{ id: string; referenceId: string }[]> {
    const cachedCustomFooters = this.customFootersCache.get(config);
    if (cachedCustomFooters) {
      return cachedCustomFooters;
    }

    const hiddenColumns = config.hiddenColumns ?? [];
    const customFooters = (config.customFooter ?? []).map((_, index) =>
      config.columns
        .map((column) => ({
          id: `customfooter_${column.id}${index}`,
          referenceId: column.id,
        }))
        .filter((footerColumn) => !hiddenColumns.includes(footerColumn.referenceId)),
    );

    this.customFootersCache.set(config, customFooters);

    return customFooters;
  }

  getCustomFooterRowIds(config: TableConfig): string[][] {
    const cachedFooterRowIds = this.customFooterRowIdsCache.get(config);
    if (cachedFooterRowIds) {
      return cachedFooterRowIds;
    }

    const footerRowIds = this.getCustomFooters(config).map((footerColumns) =>
      footerColumns.map((footerColumn) => footerColumn.id),
    );

    this.customFooterRowIdsCache.set(config, footerRowIds);

    return footerRowIds;
  }

  getFooterVisibility(config: TableConfig): boolean[] {
    const cachedFooterVisibility = this.footerVisibilityCache.get(config);
    if (cachedFooterVisibility) {
      return cachedFooterVisibility;
    }

    const footerVisibility = config.customFooter ?? [];
    this.footerVisibilityCache.set(config, footerVisibility);

    return footerVisibility;
  }

  getCustomFooterValue(values: string[] | undefined, index: number): string {
    return values?.[index] ?? '';
  }

  getFooterValue(id: string, config: TableConfig) {
    const columnData = config.columns.find((c) => c.id === id);
    if (
      columnData?.type !== TableColumnTypes.NUMBER &&
      columnData?.type !== TableColumnTypes.CURRENCY
    ) {
      return;
    } else {
      const values = config.dataSource.data.map((r: { [key: string]: number }) => r?.[id] ?? 0);

      return this.calculateFooterValue(values, columnData.footerValueTypes);
    }
  }

  dragDropped(config: TableConfig, event: CdkDragDrop<unknown>, row: unknown) {
    if (config.dragDropped) {
      config.dragDropped(event, row);
    }
  }

  private calculateFooterValue(values: number[], footerValueType?: TableFooterValueTypes) {
    switch (footerValueType) {
      case TableFooterValueTypes.MIN:
        return Math.min(...values);
      case TableFooterValueTypes.MAX:
        return Math.max(...values);
      case TableFooterValueTypes.AVERAGE:
        return values.reduce((a, b) => a + b, 0) / values.length;
      case TableFooterValueTypes.COUNT:
        return values.length;
      default:
        return values.reduce((a, b) => a + b, 0);
    }
  }

  getValue(value: number, digitsInfo: string) {
    if (value === undefined || Number.isNaN(value) || !Number.isFinite(value)) {
      return this.locale.translate('n/a');
    }
    return this.decimalPipe.transform(value, digitsInfo);
  }
}
